import { getItemsByIdList } from "@api/Items";
import { Cost, CostCurrencyEnum, EntityId, Item, Order, OrderItem, OrderOrderStatusEnum, OrderTransaction, QueueEntryObject, QueueEntryObjectStatusEnum } from "cs.node.utils/csapi/provisioning";
import { OrderItemWithInfo } from "@interfaces/Orders";
import Localization from "@localization/Index";
import { NotFoundItemInArray } from "../../../src/interfaces/Items";
import { askConfirmation, closeWaiting, showError, showWaiting, showWarning } from "./NotificationsUtils";
import { createQueueEntry, getQueuesEntriesByFilter } from "@api/Queues";
import { NavigateFunction } from "react-router-dom";
import { exportOrderExcel } from "@api/Orders";
import { format } from "date-fns";
import { OrderActivity, OrderActivityType } from "cs.node.utils/OrderUtils";
import Tracking from "@components/Tracking/Tracking";
import { useContext } from "react";
import { AuthContext } from "@providers/AuthProvider";
import { UserRoleEnum } from "cs.node.utils/RoleUtils";
import { CloudStoreShopIdEnum } from "cs.node.utils/ShopUtils";
import ShopIcon from "@components/ShopIcon/ShopIcon";
import Badge from "react-bootstrap/esm/Badge";
import { useMobileMediaQuery } from "./MediaComponents";

const orderStatuses = [
    { status: OrderOrderStatusEnum.Pending, icon: "bi-pencil-square", color: "warning"},
    { status: OrderOrderStatusEnum.Confirmed, icon: "bi-check-square", color: "success"},
    { status: OrderOrderStatusEnum.Canceled, icon: "bi-dash-square", color: "danger"},
    { status: OrderOrderStatusEnum.Refurbished, icon: "bi-arrow-repeat", color: "dark"},
    { status: OrderOrderStatusEnum.Shipped, icon: "bi-truck", color: "primary"},
    { status: OrderOrderStatusEnum.PartiallyShipped, icon: "bi-truck-flatbed", color: "info"},
    { status: OrderOrderStatusEnum.Other, icon: "bi-question-circle", color: "black"},
]

const transactionStatusThemes = [
    { status: "COMPLETED", color: "success"},
    { status: "CANCELED", color: "dark"},
    { status: "FAILED", color: "danger"},
]

enum OrderDocumentTypes {
    COMMERCIAL_INVOICE = "COMMERCIAL_INVOICE",
    AIRWAY_BILL = "AIRWAY_BILL",
    RETURN_INSTRUCTIONS = "RETURN_INSTRUCTIONS",
    RETURN_LABEL = "RETURN_LABEL",
    CITES_DECLARATION = "CITES_DECLARATION",
    ORDER_DESCRIPTION = "ORDER_DESCRIPTION",
    SHIPPING_DESCRIPTION = "SHIPPING_DESCRIPTION",
    SHIPPING_LABEL = "SHIPPING_LABEL",
    OTHER = "OTHER",
}
const orderDocumentsProperties = [
    {id: OrderDocumentTypes.COMMERCIAL_INVOICE, text: Localization.DOCUMENT_TYPES.COMMERCIAL_INVOICE, icon: "bi-receipt"},
    {id: OrderDocumentTypes.AIRWAY_BILL, text: Localization.DOCUMENT_TYPES.AIRWAY_BILL, icon: "bi-airplane"},
    {id: OrderDocumentTypes.RETURN_INSTRUCTIONS, text: Localization.DOCUMENT_TYPES.RETURN_INSTRUCTIONS, icon: "bi-arrow-counterclockwise"},
    {id: OrderDocumentTypes.RETURN_LABEL, text: Localization.DOCUMENT_TYPES.RETURN_LABEL, icon: "bi-upc-scan"},
    {id: OrderDocumentTypes.CITES_DECLARATION, text: Localization.DOCUMENT_TYPES.CITES_DECLARATION, icon: "bi-house-up"},
    {id: OrderDocumentTypes.ORDER_DESCRIPTION, text: Localization.DOCUMENT_TYPES.ORDER_DESCRIPTION, icon: "bi-clipboard-data"},
    {id: OrderDocumentTypes.SHIPPING_DESCRIPTION, text: Localization.DOCUMENT_TYPES.SHIPPING_DESCRIPTION, icon: "bi-truck"},
    {id: OrderDocumentTypes.SHIPPING_LABEL, text: Localization.DOCUMENT_TYPES.SHIPPING_LABEL, icon: "bi-upc"},
    {id: OrderDocumentTypes.OTHER, text: Localization.DOCUMENT_TYPES.OTHER, icon: "bi-question-circle"}
]

const getOrderDocumentProperties = (orderDocumentType: OrderDocumentTypes) =>{
    return orderDocumentsProperties.find(orderDocumentProperties=>orderDocumentProperties.id === orderDocumentType) ?? orderDocumentsProperties.find(orderDocumentProperties=>orderDocumentProperties.id === OrderDocumentTypes.OTHER)!
}

/**
 * Get all the order status rendering elements
 * @param status the order status
 */
const getOrderStatus = (status?:OrderOrderStatusEnum)=>{
    return orderStatuses.find(orderStatus=>orderStatus.status===status) ?? orderStatuses.find(orderStatus=>orderStatus.status===OrderOrderStatusEnum.Other)!
}

const getTransactionStatusTheme = (status?:string) =>{
    return transactionStatusThemes.find(theme=>theme.status===status) ?? { status: "N.D.", color: "black"}
}

const getOrderItemsWithInfo = async (orderItems:OrderItem[], lang:string)=>{
    try {
        const orderItemsIds = (orderItems?.map(item=>item.item_id?.$oid) ?? []) as string[]
        const items = await getItemsByIdList(orderItemsIds)
        const orderItemsWithInfo:OrderItemWithInfo[] = items.map(item=>{
            const notFoundItem = item as NotFoundItemInArray
            if(notFoundItem.index!== undefined) {
                // manage return value of not found element
                const orderItemNotFoundOriginal = orderItems?.[notFoundItem.index]
                const { item_id, sku, price_adjustments, total_price_tax_incl, price, qty, unit_price_tax_incl, packaging_info, attributes  } = orderItemNotFoundOriginal!
                const { duty_amount, duty_currency_code } = attributes ?? {}
                const duty: Cost | undefined = duty_amount && duty_currency_code? {amount: duty_amount as unknown as number, currency: duty_currency_code as unknown as CostCurrencyEnum}: undefined

                return {
                    is_in_cs: false,
                    id: item_id?.$oid ?? "",
                    sku,
                    img: "",
                    skuParent: "N.D.",
                    description: "N.D.",
                    brand: "N.D.",
                    season: "N.D.",
                    variation: price_adjustments?.[0]?.reason_id,
                    variationType: price_adjustments?.[0]?.reason_title,
                    qty,
                    cost: {amount: price, currency: total_price_tax_incl?.currency ?? "EUR"},
                    unit_price: {amount: unit_price_tax_incl?.amount, currency: unit_price_tax_incl?.currency ?? "EUR"},
                    duty,
                    packageTypeId: packaging_info?.entries?.[0]?.package_type_id?.$oid,
                    listing_excluded_shops: []
                }
            }

            const foundItem = item as Item

            const foundOrderItemOriginal = orderItems?.find(i=>i.item_id?.$oid===foundItem.item_id?.$oid)
            const { price_adjustments, total_price_tax_incl, price, qty, unit_price_tax_incl, packaging_info, attributes  } = foundOrderItemOriginal!
            const { duty_amount, duty_currency_code } = attributes ?? {}
            const duty: Cost | undefined = duty_amount && duty_currency_code? {amount: duty_amount as unknown as number, currency: duty_currency_code as unknown as CostCurrencyEnum}: undefined

            const title = foundItem?.locs?.singles?.title?.[lang] ?? (foundItem?.locs?.singles?.title?.["it"] ?? foundItem?.props?.['title'])
            const listing_excluded_shops = foundItem.abs?.reduce((acc: EntityId[], absItem)=>{
                if(absItem.lst_excl_shops && absItem.lst_excl_shops.length > 0) {
                    acc.push(...absItem.lst_excl_shops)
                }
                return acc
            }, [] as EntityId[])

            return {
                is_in_cs: true,
                id: foundItem.item_id?.$oid ?? "",
                sku: foundItem.sku,
                img: foundItem.imgs?.[0].url ?? "",
                skuParent: (foundItem.props?.sku_parent ?? "N.D.") as string,
                description: (title ?? "N.D.") as string,
                brand: (foundItem.props?.brand ?? "N.D.") as string,
                season: (foundItem.props?.season ?? "N.D.") as string,
                variation: price_adjustments?.[0]?.reason_id,
                variationType: price_adjustments?.[0]?.reason_title,
                qty,
                cost: {amount: price, currency: total_price_tax_incl?.currency ?? "EUR"},
                unit_price: {amount: unit_price_tax_incl?.amount, currency: unit_price_tax_incl?.currency ?? "EUR"},
                cat_ids: foundItem.cats,
                barcode: foundItem.barcodes?.[0].code ?? (foundItem.props?.barcode as unknown as string),
                duty,
                packageTypeId: packaging_info?.entries?.[0]?.package_type_id?.$oid,
                listing_excluded_shops
            }
        })

        return orderItemsWithInfo
    } catch(err) {
        console.log(err)
        throw err
    }
}

const checkOrderSent = async(order: Order|undefined, type: "ERP" | "NS", navigate: NavigateFunction) => {
    if(!order?._id?.$oid) {
        return
    }
    try{
        showWaiting(Localization.ATTENDERE)
        const queueId = type==="ERP"? "erp.outbound.order": "ns.outbound.order"
        const response = await getQueuesEntriesByFilter({queueId, externalKey: order._id.$oid, pageIndex: 0, pageSize: 2500, sort: "updated_dt[DESC]"})
        const entry = response.content?.[0]
        closeWaiting()
        if(!entry?._id?.$oid) {
            return showWarning(Localization.NESSUN_ELEMENTO_PRESENTE, Localization.ATTENZIONE)
        }
        const entryId = entry._id?.$oid

        return navigate(`/queues/${queueId}/${entryId}`)

    } catch(err) {
        closeWaiting()
        showError(Localization.ERRORE_GENERICO, err)
    }
}

const createQueueEntryForOrder = async(order: Order|undefined, type: "ERP" | "NS", navigate: NavigateFunction) => {
    if(!order?._id?.$oid) {
        return
    }
    try{
        const askResponse = await askConfirmation({
            text: (type==="ERP")?Localization.PAGE_ORDER.MENU.CONFERMA_INVIO_ORDINE_ERP:Localization.PAGE_ORDER.MENU.CONFERMA_INVIO_ORDINE_NETSUITE
        })

        if(!askResponse.isConfirmed) {
            return
        }

        showWaiting(Localization.ATTENDERE)
        const queueId = type==="ERP"? "erp.outbound.order": "ns.outbound.order"
        const entry: QueueEntryObject = {
            status : QueueEntryObjectStatusEnum.ToBeProcessed,
            partition_id : "default",
            external_key : order._id.$oid,
            search_keys : [ 
                order._id.$oid
            ],
            payload : order._id as unknown as object,
            entity_id: "",
            creation_correlation_uuid: ""
        }

        const response = await createQueueEntry(queueId, entry)

        const successConfirmationResponse = await askConfirmation({
            text: Localization.OPERAZIONE_AVVENUTA_CON_SUCCESSO, 
            icon: "success",
            confirmButtonText: Localization.PAGE_ORDER.MENU.VAI_A_ELEMENTO_DELLA_CODA,
            cancelButtonText: Localization.CHIUDI
        })

        if(!successConfirmationResponse.isConfirmed) {
            return
        }
        const entryId = response.content?._id?.$oid!
        return navigate(`/queues/${queueId}/${entryId}`)

    } catch(err) {
        closeWaiting()
        showError(Localization.ERRORE_GENERICO, err)
    }
}

const downloadOrderExcel = async(order: Order | undefined)=>{
    if(!order?._id?.$oid) {
        return
    }
    
    try{
        showWaiting(Localization.ATTENDERE)
        const response = await exportOrderExcel(order?._id?.$oid!)

        closeWaiting()
        // create file link in browser's memory
        const href = URL.createObjectURL(response)

        // create "a" HTML element with href to file & click
        const link = document.createElement('a')
        link.href = href

        link.setAttribute('download', `order_${order?.shop_order_id}_${format(new Date(), 'yyyyMMddHHmmss')}.xlsx`)
        document.body.appendChild(link)
        link.click()

        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link)
        URL.revokeObjectURL(href)

    } catch(err) {
        closeWaiting()
        showError(Localization.ERRORE_GENERICO, err)
    }
}

const getOrderAddressNominee = (firstName: string="", lastName: string="", companyName: string="") =>{
    if(companyName.length > 0) {
        return companyName
    }
    if(firstName.length === 0 && lastName.length > 0) {
        return lastName
    }
    if(firstName.length > 0 && lastName.length === 0) {
        return firstName
    }
    if(firstName.length > 0 && lastName.length > 0) {
        return `${firstName} ${lastName}`
    }
    return "N.D."
}

const getActivityTitleStatusHistory = (activity: OrderActivity) =>{
    const { payload } = activity
    const { status } = payload as { from: string, status: OrderOrderStatusEnum }
    const statusTheme = getOrderStatus(status as OrderOrderStatusEnum)
    return Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.STATUS_HISTORY(status, statusTheme.color)
}

const ActivityTitleReplyData = ({activity}: {activity: OrderActivity}) =>{
    const auth = useContext(AuthContext)
    const role = auth.userData?.role
    const isMobile = useMobileMediaQuery()

    const { payload } = activity
    const correlation_uuid = (payload as any)?.correlation_uuid
    return <div className="d-flex align-items-center justify-content-start">
        {Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.ACCETTAZIONE_ORDINE}
        {correlation_uuid && role === UserRoleEnum.SUPPORT && isMobile === false && <Tracking correlation_uuid={correlation_uuid} />}
    </div>
}

const getMiintoTitle = (title: string) =>{
    return <div className="d-flex align-items-end justify-content-start">
        <ShopIcon extended id={CloudStoreShopIdEnum.Miinto} size={16} />
        <span className="ms-3">{title}</span>
    </div>
}

const getMiintoDocumentTitle = (activity: OrderActivity) =>{
    const { payload } = activity as { payload: QueueEntryObject }

    let documentTypeTheme
    switch((payload?.payload as any)?.documentType) {
        case "LABEL_RETURN":
            documentTypeTheme = getOrderDocumentProperties(OrderDocumentTypes.RETURN_LABEL)
            break
        case "LABEL_SHIPPING":
            documentTypeTheme = getOrderDocumentProperties(OrderDocumentTypes.SHIPPING_LABEL)
            break
        case "ORDER_INFO":
            documentTypeTheme = getOrderDocumentProperties(OrderDocumentTypes.ORDER_DESCRIPTION)
            break
        case "RETURN_INFO":
            documentTypeTheme = getOrderDocumentProperties(OrderDocumentTypes.RETURN_INSTRUCTIONS)
            break
        default:
            documentTypeTheme = getOrderDocumentProperties(OrderDocumentTypes.OTHER)
    }

    return <div className="d-flex align-items-center justify-content-start">
        <ShopIcon extended id={CloudStoreShopIdEnum.Miinto} size={16} />
        <span className="ms-3">{Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.RICHIESTA_DOCUMENTO_SPEDIZIONE}:</span>
        <Badge bg="secondary" className="ms-2 text-black"><i className={`${documentTypeTheme.icon}`} style={{fontSize: 14}} /> <span className="fw-600 ms-2">{documentTypeTheme.text}</span></Badge>
    </div>

}

const getNetSuiteTitle = (title: string) =>{
    return <div className="d-flex align-items-end justify-content-start">
        <img src={"/images/netsuite.png"} alt="Netsuite" style={{width: 78, height: 21}} />
        <span className="ms-3">{title}</span>
    </div>
}

const getActivityTitle = (activity: OrderActivity) =>{
    switch(activity.type) {
        case OrderActivityType.StatusHistory:
            return getActivityTitleStatusHistory(activity)
        case OrderActivityType.ReplyData:
            return <ActivityTitleReplyData activity={activity} />
        case OrderActivityType.ERPOutboundOrder:
            return Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.INVIO_ORDINE_ERP
        case OrderActivityType.NetSuiteOutboundOrder:
            return getNetSuiteTitle(Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.INVIO_ORDINE_NETSUITE)
        case OrderActivityType.OutboundPackagingInfo:
            return Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.INVIO_INFORMAZIONI_PACKAGING
        case OrderActivityType.OutboundShipmentInfo:
            return Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.INVIO_INFORMAZIONI_SPEDIZIONE
        case OrderActivityType.MiintoBookShipping:
            return getMiintoTitle(Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.PRENOTAZIONE_SPEDIZIONE)
        case OrderActivityType.MiintoFetchShippingDocument:
            return getMiintoDocumentTitle(activity)
        case OrderActivityType.Transaction:
            const { payload } = activity
            const transaction = payload as OrderTransaction
            return Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.TRANSAZIONE_DI_TIPO(transaction.transaction_type)
        case OrderActivityType.Shipment:
            return Localization.PAGE_ORDER.TAB_ACTIVITIES.TITLES.SPEDIZIONE
        default:
            return "N.D."
    }
}

const getActivityIcon = (type: OrderActivityType) =>{
    switch(type) {
        case OrderActivityType.StatusHistory:
            return "bi-arrow-left-right"
        case OrderActivityType.ReplyData:
            return "bi-hand-thumbs-up"
        case OrderActivityType.ERPOutboundOrder:
            return "bi-box-arrow-up-right"
        case OrderActivityType.NetSuiteOutboundOrder:
            return "bi-box-arrow-up-right"
        case OrderActivityType.OutboundShipmentInfo:
            return "bi-truck"
        case OrderActivityType.OutboundPackagingInfo:
            return "bi-box-seam"
        case OrderActivityType.MiintoBookShipping:
            return "bi-truck-flatbed"
        case OrderActivityType.MiintoFetchShippingDocument:
            return "bi-file-earmark-text"
        case OrderActivityType.Transaction:
            return "bi-currency-exchange"
        case OrderActivityType.Shipment:
            return "bi-truck"
        default:
            return "bi-question-circle"
    }
}

const getActivityQueueId = (type: OrderActivityType) =>{
    switch(type) {
        case OrderActivityType.ERPOutboundOrder:
            return "erp.outbound.order"
        case OrderActivityType.NetSuiteOutboundOrder:
            return "ns.outbound.order"
        case OrderActivityType.OutboundShipmentInfo:
            return "other.outbound.shipments"
        case OrderActivityType.OutboundPackagingInfo:
            return "other.outbound.packages"
        case OrderActivityType.MiintoBookShipping:
        case OrderActivityType.MiintoFetchShippingDocument:
            return "core.shops.miinto.async-request"
        default:
            return ''
    }
}

export { 
    getOrderStatus,
    getTransactionStatusTheme,
    orderStatuses,
    OrderDocumentTypes,
    orderDocumentsProperties,
    getOrderDocumentProperties,
    getOrderItemsWithInfo,
    checkOrderSent,
    createQueueEntryForOrder,
    downloadOrderExcel,
    getOrderAddressNominee,
    getActivityTitle,
    getActivityIcon,
    getActivityQueueId
}